import {
    Container,
    Button,
    ShowFor,
    Size,
    HeadstartLogo,
    HideFor,
} from "@brightagency/headstart-common";
import LocalPhoneRoundedIcon from "@mui/icons-material/LocalPhoneRounded";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import { isEmptyArray } from "formik";
import React, { useEffect, useMemo } from "react";
import { useHistory } from "react-router";
import { Link } from "react-router-dom";
import useResizeObserver from "use-resize-observer";
import { WPPost } from "../../../constants/types";
import { useBreakpoint } from "../../../controllers";
import {
    useCurrentRouteData,
    useGlobalAcf,
    useGlobalLinks,
    usePostTypes,
} from "../../../services/app-state";
import { useAssessmentState } from "../../../services/assessment-state";
import { Screens } from "../../../services/assessment-state/types";
import { getAcfPostUrl, getWpPostUrl, useSuburbName } from "../../../utils";
import { useScrollYPosition } from "../../../utils/hooks";
import { IconAction } from "../../atoms/IconAction";
import { NotificationBar } from "../../molecules/NotificationBar";
import { useTemplateThemeVariations, templateFileNames } from "../../templates/PageTemplateChooser";
import { MenuItems } from "./MenuItems";
import { MobileMenu } from "./MobileMenu";
import {
    Outer,
    Inner,
    LogoContainer,
    Links,
    Buttons,
    MenuButtonWrapper,
    NeedHelpButtonText,
    // TranslateIconLink,
} from "./styled";
import { LinkItem, MenuItem } from "./types";

export const Nav = () => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
    const scrolled = useScrollYPosition(0);
    const page = useCurrentRouteData() as WPPost | undefined;
    const [, { getGlobalLink }] = useGlobalLinks();
    const isNeedHelpNowPage = page?.template === templateFileNames.needHelpNow;
    const [{ custom_page_titles }] = useGlobalAcf();
    const [postTypes] = usePostTypes();
    const isDesktop = useBreakpoint(Size.medium);
    const mapPostTypeItemToMenuItem = (post: WPPost): LinkItem => ({
        type: "link",
        title: post.title.rendered,
        to: getWpPostUrl(post),
    });

    const menuItems: MenuItem[] = useMemo(() => {
        const links: MenuItem[] = [
            {
                type: "link",
                title: custom_page_titles.populations,
                to: `/#support-for-anyone`,
            },
            {
                type: "link",
                title: custom_page_titles.conditions,
                to: `/#disorders-concerns`,
            },
            {
                type: "link",
                title: custom_page_titles.challenging_situations,
                to: `/#challenging-situations`,
            },
        ];
        return links;
    }, [custom_page_titles]);

    const suburbName = useSuburbName();

    const mobileMenuItems: MenuItem[] = useMemo(() => {
        const links: MenuItem[] = [
            // {
            //     type: "link",
            //     title: (
            //         <TranslateIconLink>
            //             <TranslateRoundedIcon />
            //             {getGlobalLink("languages_page").post_title}
            //         </TranslateIconLink>
            //     ),
            //     to: getAcfPostUrl(getGlobalLink("languages_page")),
            // },
            {
                type: "dropdown",
                title: custom_page_titles.populations,
                children: postTypes.populations.map(mapPostTypeItemToMenuItem),
            },
            {
                type: "dropdown",
                title: custom_page_titles.conditions,
                children: postTypes.conditions.map(mapPostTypeItemToMenuItem),
            },
            {
                type: "dropdown",
                title: custom_page_titles.challenging_situations,
                children: postTypes.challengingSituations.map(mapPostTypeItemToMenuItem),
            },
        ];
        
        if(suburbName && !isEmptyArray(postTypes.localInformation.map(mapPostTypeItemToMenuItem))){
            links.push({
                type: "dropdown",
                title: custom_page_titles.local_information,
                children: postTypes.localInformation.map(mapPostTypeItemToMenuItem),
            });
        }
        const page = getGlobalLink("for_health_professionals");
        links.push({
            type: "link",
            title: page.post_title,
            to: getAcfPostUrl(page),
        });
        return links;
    }, [
        suburbName,
        custom_page_titles.populations,
        custom_page_titles.conditions,
        custom_page_titles.challenging_situations,
        custom_page_titles.local_information,
        postTypes.populations,
        postTypes.conditions,
        postTypes.challengingSituations,
        postTypes.localInformation,
        getGlobalLink,
    ]);

    const { ref, height = 80 } = useResizeObserver();

    useEffect(() => {
        document.body.style.setProperty("--nav-height", `${height}px`);
    }, [height]);

    const history = useHistory();

    const { state } = useAssessmentState();
    const onResults = state.currentScreen == Screens.Results;
    const {
        shouldHideNavUnderline,
        shouldUseFixedHeader,
        shouldUseDarkNavBar,
    } = useTemplateThemeVariations();
    const hideBottomBorder = shouldHideNavUnderline && !onResults;
    const useFixedHeader = shouldUseFixedHeader || onResults;

    return (
        <Outer
            ref={ref}
            scrolled={scrolled}
            fixedHeader={useFixedHeader}
            shouldHideBottomBorder={hideBottomBorder}
            useDarkBackground={shouldUseDarkNavBar}
        >
            <NotificationBar />
            <Container>
                <Inner>
                    <LogoContainer>
                        <Link
                            to="/"
                            style={{
                                display: "block",
                                lineHeight: 0,
                            }}
                            onClick={() => {
                                if (
                                    history.location.pathname === "/" ||
                                    history.location.pathname === ""
                                ) {
                                    window.scrollTo({ top: 0, behavior: "smooth" });
                                }
                            }}
                        >
                            <HeadstartLogo explicitSize={false} />
                        </Link>
                    </LogoContainer>
                    <ShowFor size={Size.large}>
                        <Links>
                            <MenuItems items={menuItems} onNavigate={() => {}} />
                        </Links>
                    </ShowFor>
                    <Buttons>
                        <ShowFor size={Size.large}>
                            <Button
                                appearance="outline"
                                size="small"
                                to={getAcfPostUrl(getGlobalLink("not_sure_where_to_begin_page"))}
                            >
                                Not sure where to begin?
                            </Button>
                        </ShowFor>
            
                        {!isNeedHelpNowPage && (
                            <Button
                                appearance="secondary"
                                size={isDesktop ? "small" : "xsmall"}
                                to={getAcfPostUrl(getGlobalLink("i_need_help_now_page"))}
                                iconBefore={
                                    <LocalPhoneRoundedIcon
                                        fontSize="small"
                                        style={{ marginTop: 2 }}
                                    />
                                }
                            >
                                <NeedHelpButtonText>Get help now</NeedHelpButtonText>
                            </Button>
                        )}
                    </Buttons>
                    
                    <HideFor size={Size.large}>
                        <MenuButtonWrapper>
                            <IconAction
                                icon={<MenuRoundedIcon />}
                                onClick={() => setIsMobileMenuOpen(true)}
                            >
                                Menu
                            </IconAction>
                        </MenuButtonWrapper>
                    </HideFor>
                </Inner>
            </Container>
            <MobileMenu
                items={mobileMenuItems}
                isOpen={isMobileMenuOpen}
                onClose={() => setIsMobileMenuOpen(false)}
            />
        </Outer>
    );
};
